import {injectable} from "tsyringe";
import {transformer} from "../decorators/transformer.decorator";
import {TransformerInterface} from "./transformerInterface";
import {CommandDTO} from "../domain/dtos/command.dto";
import {UpdateLinkTileCommand, UpdateLinkTilePayload} from "../commands/update-link-tile.command";

/**
 * Serialises a UpdateLinkTileCommand into the backend command DTO.
 *
 * A dedicated transformer is REQUIRED, not a nicety. Without one the generic
 * command→DTO fallback runs, and that fallback demands a change-tracked proxy
 * as the payload — these commands carry a plain object, so it throws
 * "The payload of the command must be a proxified object". The throw happens
 * OUTSIDE the CommandBus's try block, which wedges the bus for the rest of the
 * session rather than failing the one command.
 *
 * The full envelope is emitted, not just the payload: inside a bulk each
 * sub-command is re-built through the command factory on the server, so every
 * element needs `name`, `uid`, `mainEntityClass` and `mainEntityUid` exactly
 * like an outer command.
 *
 * ADR-015 CLASSIFIES THIS AS AN ENTITY COMMAND, and an entity command is meant
 * to lose its own transformer and travel the generic change-tracked path. It
 * cannot today, and the two reasons are not stylistic — deleting this file
 * without addressing BOTH of them reproduces the bus wedge the ADR describes:
 *
 * 1. There is no tracked tile to send. `dashboard-creator._collectTiles()`
 *    rebuilds every tile as a fresh plain object at save time, out of the
 *    Gridstack layout plus a draft map, and `ChangeTracker` seeds its original
 *    values in its constructor — so a proxy made over such an object reports an
 *    empty change set and the update would silently write nothing. A real delta
 *    needs the tile to live as ONE mutated proxy from the moment it is loaded,
 *    which is a different state model for the board, not an addition to it.
 * 2. The backend does not accept a delta. `UpdateLinkTileCommandHandler`
 *    requires a non-empty `text` and, by documented design in
 *    `LinkTilePayload`, reads an ABSENT optional field as "clear it". A
 *    change-only payload therefore either fails outright or wipes fields the
 *    user never touched.
 */
@injectable()
@transformer({
    source: UpdateLinkTileCommand,
    target: `dto`
})
export class UpdateLinkTileCommandToDtoTransformer implements TransformerInterface<CommandDTO<UpdateLinkTilePayload>, UpdateLinkTileCommand> {

    public async transform(command: UpdateLinkTileCommand): Promise<CommandDTO<UpdateLinkTilePayload>> {
        return <CommandDTO<UpdateLinkTilePayload>>{
            uid: command.uid,
            name: command.name,
            mainEntityClass: command.mainEntityClass,
            mainEntityUid: command.mainEntityUid,
            payload: command.payload,
        };
    }
}
